import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { 
  Heart, Frown, Sunrise, Sparkles, Users, MessageCircle, Coffee, Briefcase, HeartPulse,
  Globe, MapPin, UserPlus, Trophy, Megaphone, Coins, Pin,
  ChevronLeft, Search, ThumbsUp, MessageSquare, Eye, Clock, PenSquare, Gift, ChevronRight,
  Flame, Hand, HelpCircle, Crown, ShieldCheck, Calendar, X, ChevronDown, Image, Video,
  Plane, Palette, Sun, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { AuthorProfilePopover } from "../components/AuthorProfilePopover";
import { useAuth } from "../context/AuthContext";
import AuthModal from "../components/AuthModal";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const saveScrollPosition = () => {
  sessionStorage.setItem("community_scroll", window.scrollY.toString());
};

const MyPostsCard = ({ isAuthenticated, setShowAuth }) => {
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    if (!isAuthenticated && !localStorage.getItem("liztv_token")) {
      setShowAuth(true);
      return;
    }
    navigate("/my-corner");
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="block w-full text-left bg-gradient-to-br from-stone-400 to-stone-500 rounded-2xl p-5 cursor-pointer hover:from-stone-300 hover:to-stone-400 transition-all overflow-hidden relative"
      data-testid="ai-match-writing-cta"
    >
      <div className="absolute top-3 right-3 w-20 h-20 bg-white/10 rounded-full pointer-events-none" />
      <div className="absolute top-3 right-3 animate-pulse">
        <Sparkles className="w-4 h-4 text-amber-300" />
      </div>
      <PenSquare className="w-8 h-8 text-burgundy-200 mb-3" />
      <p className="text-white font-bold text-sm mb-1">글로 만나는 나의 인연 찾기</p>
      <p className="text-stone-50/90 text-xs mb-3 leading-relaxed">당신의 글이 인연의<br />시작이 됩니다.</p>
      <span className="inline-block bg-amber-500/30 text-amber-50 text-xs font-medium px-3 py-1 rounded-full" data-testid="my-corner-detail-btn">자세히 보기 &rarr;</span>
    </button>
  );
};


const QuickPostComposer = ({ user, token, isAuthenticated, setShowAuth, onPostCreated, navigate }) => {
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("daily");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    // 페이지 로드 시 textarea에 자동 포커스 (커서 깜박임)
    const t = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus({ preventScroll: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, []);

  const composerCategories = [
    { id: "daily", title: "일상&취미" },
    { id: "love", title: "연애와 썸" },
    { id: "breakup", title: "이별&상처" },
    { id: "advice", title: "고민상담" },
    { id: "self", title: "자기관리&매력" },
    { id: "friends", title: "친구&모임" },
    { id: "life", title: "인생2막 노후계획" },
    { id: "career", title: "직장&커리어" },
    { id: "health", title: "건강&뷰티" },
    { id: "global", title: "글로벌 싱글라이프" },
    { id: "date", title: "데이트 코스추천" },
    { id: "intro", title: "자기소개 릴레이" },
    { id: "welcome", title: "가입인사" },
  ];

  const handleFocus = () => {
    setExpanded(true);
  };

  const requireLogin = () => {
    if (!isAuthenticated && !localStorage.getItem("liztv_token")) {
      setShowAuth(true);
      return true;
    }
    return false;
  };

  const handleChange = (e) => {
    if (requireLogin()) {
      e.target.blur();
      return;
    }
    setContent(e.target.value);
  };

  const handleSubmit = async () => {
    // 명확한 토큰 체크 (stale isAuthenticated 방지)
    const stored = localStorage.getItem("liztv_token");
    if (!token && !stored) {
      setShowAuth(true);
      return;
    }
    const trimmed = content.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const firstLine = trimmed.split("\n")[0].trim();
      const title = firstLine.length > 50 ? firstLine.slice(0, 50) + "..." : firstLine;
      const body = trimmed.length > firstLine.length ? trimmed.slice(firstLine.length).trim() : trimmed;
      const headers = { "Content-Type": "application/json" };
      const bearer = token || stored;
      if (bearer) headers["Authorization"] = `Bearer ${bearer}`;
      const res = await fetch(`${API_URL}/api/posts`, {
        method: "POST",
        headers,
        body: JSON.stringify({ category, title, content: body || trimmed, images: [], videos: [] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "글 등록에 실패했습니다.");
      }
      const newPost = await res.json();
      // 익명으로 등록되었다면 토큰 문제이므로 사용자에게 알림
      if (newPost && (newPost.author === "익명" || !newPost.author_id)) {
        toast.error("로그인이 만료되어 익명으로 등록될 뻔했습니다. 다시 로그인 후 작성해주세요.");
        // 글 자체는 등록됨; 사용자가 다시 작성하도록 안내
      } else {
        toast.success("글이 등록되었습니다.");
      }
      setContent("");
      setExpanded(false);
      if (onPostCreated) onPostCreated(newPost);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = user?.nickname || user?.username || user?.name || (user?.email ? user.email.split("@")[0] : "익명");
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 mb-3 w-full max-w-[91%]" data-testid="quick-post-composer">
      <div className="flex gap-3">
        {/* Avatar */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
          user?.membership_type === 'vip' ? 'bg-purple-400' :
          user?.membership_type === 'premium' ? 'bg-amber-400' :
          user?.membership_type === 'regular' ? 'bg-emerald-400' : 'bg-stone-300'
        }`}>
          {user?.profile_image ? (
            <img src={user.profile_image} alt={user.username} className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-bold text-sm">{initial}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Author + Category */}
          <div className="flex items-center gap-1.5 mb-2">
            <span className="font-bold text-stone-800 text-[15px]" data-testid="composer-author">
              {displayName}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-stone-300" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              onFocus={handleFocus}
              className="text-xs text-stone-500 bg-transparent border-none focus:outline-none cursor-pointer hover:text-burgundy-500 transition-colors"
              data-testid="composer-category-select"
            >
              {composerCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="오늘 어떤 이야기를 나눠볼까요?"
            rows={expanded ? 3 : 2}
            className="w-full resize-none bg-stone-50 hover:bg-stone-100 focus:bg-white focus:ring-2 focus:ring-burgundy-500/20 rounded-xl border border-stone-100 focus:border-burgundy-200 focus:outline-none text-sm text-stone-800 placeholder:text-stone-400 leading-relaxed px-3.5 py-2.5 transition-all"
            style={{ minHeight: expanded ? "72px" : "38px" }}
            maxLength={2000}
            data-testid="composer-textarea"
          />

          {/* Toolbar - always visible */}
          <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-stone-100">
            <div className="flex items-center gap-0.5 text-stone-400">
              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated && !localStorage.getItem("liztv_token")) { setShowAuth(true); return; }
                  navigate("/community/write?category=" + category + (content ? `&prefill=${encodeURIComponent(content)}` : "") + "&attach=image");
                }}
                className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-stone-100 hover:text-burgundy-500 transition-colors"
                title="사진 첨부"
                data-testid="composer-image-btn"
              >
                <Image className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated && !localStorage.getItem("liztv_token")) { setShowAuth(true); return; }
                  navigate("/community/write?category=" + category + (content ? `&prefill=${encodeURIComponent(content)}` : "") + "&attach=video");
                }}
                className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-stone-100 hover:text-burgundy-500 transition-colors"
                title="동영상 첨부"
                data-testid="composer-video-btn"
              >
                <Video className="w-4 h-4" />
              </button>
              {expanded && (
                <span className="text-[10px] text-stone-300 ml-1.5">{content.length}/2000</span>
              )}
            </div>
            {expanded && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setContent(""); setExpanded(false); }}
                  className="text-xs text-stone-500 hover:text-stone-700 px-2 py-1"
                  data-testid="composer-cancel-btn"
                >
                  취소
                </button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !content.trim()}
                  className="btn-primary text-xs px-3 py-1 h-7 rounded-full disabled:opacity-50"
                  data-testid="composer-submit-btn"
                >
                  {submitting ? "등록 중..." : "게시"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



const restoreScrollPosition = () => {
  const saved = sessionStorage.getItem("community_scroll");
  if (saved) {
    setTimeout(() => window.scrollTo(0, parseInt(saved)), 100);
    sessionStorage.removeItem("community_scroll");
  }
};

const categories = [
  // 전체 보기 (entry tab)
  { id: "all", title: "전체글", icon: MessageSquare, color: "text-stone-600", bgColor: "bg-stone-100" },
  // === SNS 커뮤니티 14개 카테고리 (중년 친화) ===
  { id: "notice", title: "공지", icon: Megaphone, color: "text-red-600", bgColor: "bg-red-50" },
  { id: "welcome", title: "가입인사", icon: UserPlus, color: "text-emerald-600", bgColor: "bg-emerald-50" },
  { id: "love", title: "연애", icon: Heart, color: "text-rose-600", bgColor: "bg-rose-50" },
  { id: "travel", title: "여행", icon: Plane, color: "text-sky-600", bgColor: "bg-sky-50" },
  { id: "health", title: "건강", icon: HeartPulse, color: "text-pink-600", bgColor: "bg-pink-50" },
  { id: "hobby", title: "취미", icon: Palette, color: "text-violet-600", bgColor: "bg-violet-50" },
  { id: "life", title: "노후 & 인생2막", icon: Sunrise, color: "text-orange-600", bgColor: "bg-orange-50" },
  { id: "today", title: "오늘의 하루", icon: Sun, color: "text-amber-600", bgColor: "bg-amber-50" },
  { id: "free", title: "자유 이야기", icon: Coffee, color: "text-stone-700", bgColor: "bg-stone-100" },
  { id: "advice", title: "익명 고민상담", icon: Lock, color: "text-indigo-600", bgColor: "bg-indigo-50" },
  { id: "friends", title: "친구 & 모임", icon: Users, color: "text-blue-600", bgColor: "bg-blue-50" },
  { id: "zoom", title: "줌모임 / 라이브", icon: Video, color: "text-purple-600", bgColor: "bg-purple-50" },
  { id: "date", title: "데이트 코스 추천", icon: MapPin, color: "text-rose-500", bgColor: "bg-rose-50" },
  { id: "review", title: "가입후기", icon: Sparkles, color: "text-teal-600", bgColor: "bg-teal-50" },
];

// 공지글 (전체글에서 상단 고정)
const noticePosts = [
  {
    id: "notice-1",
    category: "notice",
    categoryName: "공지",
    title: "[포인트 안내] 활동하고 포인트 받으세요! (1P = 1원)",
    content: "게시글 작성 +100P, 댓글 작성 +50P, 출석 체크 +30P, 성공스토리 등록 +500P! 적립한 포인트는 멤버십 결제 시 현금처럼 사용 가능합니다.",
    author: "New Coupling 운영팀",
    date: "2026.03.27",
    views: 1523,
    likes: 234,
    comments: 45,
    isPinned: true,
    isNotice: true,
  },
  {
    id: "notice-2",
    category: "notice",
    categoryName: "공지",
    title: "[포인트 사용] 멤버십 결제 시 포인트로 할인받으세요!",
    content: "정회원, 프리미엄, VIP 멤버십 결제 시 적립한 포인트를 현금처럼 사용할 수 있습니다. 포인트 + 카드 결합 결제도 가능!",
    author: "New Coupling 운영팀",
    date: "2026.03.27",
    views: 1289,
    likes: 187,
    comments: 32,
    isPinned: true,
    isNotice: true,
  },
  {
    id: "notice-3",
    category: "notice",
    categoryName: "공지",
    title: "[이벤트] 출석 체크하고 매일 30P 받아가세요!",
    content: "매일 출석 체크만 해도 30P 적립! 포인트 페이지에서 출석 체크 버튼을 눌러주세요. 꾸준히 모으면 멤버십 할인 혜택까지!",
    author: "New Coupling 운영팀",
    date: "2026.03.26",
    views: 987,
    likes: 156,
    comments: 28,
    isPinned: true,
    isNotice: true,
  },
];

const samplePosts = [
  {
    id: 1,
    category: "love",
    categoryName: "연애와 썸",
    title: "50대에 다시 시작하는 연애, 설레면서도 두려워요",
    content: "이혼한 지 5년이 지났는데, 최근에 좋은 분을 만났어요. 그런데 다시 시작하는 게 무섭기도 하고...",
    author: "새출발",
    author_membership: "premium",
    date: "2026.03.18",
    views: 234,
    likes: 45,
    comments: 23,
  },
  {
    id: 2,
    category: "advice",
    categoryName: "고민상담",
    title: "자녀들에게 재혼 이야기를 어떻게 꺼내야 할까요?",
    content: "성인이 된 자녀들이 있는데, 재혼을 생각하고 있어요. 어떻게 말을 꺼내야 할지 고민입니다.",
    author: "행복찾기",
    author_membership: "regular",
    date: "2026.03.18",
    views: 189,
    likes: 32,
    comments: 41,
  },
  {
    id: 3,
    category: "self",
    categoryName: "자기관리&매력",
    title: "중년 남성 패션 추천해주세요!",
    content: "첫 만남 데이트가 있는데, 어떤 옷을 입어야 할지 모르겠어요. 너무 젊어 보여도, 너무 늙어 보여도 안 될 것 같고...",
    author: "멋쟁이아저씨",
    author_membership: "vip",
    date: "2026.03.17",
    views: 312,
    likes: 67,
    comments: 35,
  },
  {
    id: 4,
    category: "breakup",
    categoryName: "이별&상처",
    title: "오랜 연인과 헤어졌어요. 마음이 너무 아프네요.",
    content: "3년을 만났는데 결국 이별하게 됐어요. 나이가 들어서 그런지 회복이 더 힘든 것 같아요.",
    author: "회복중",
    author_membership: "basic",
    date: "2026.03.17",
    views: 456,
    likes: 89,
    comments: 67,
  },
  {
    id: 5,
    category: "life",
    categoryName: "인생2막노후",
    title: "은퇴 후 새로운 취미를 시작했어요",
    content: "회사 다닐 때는 시간이 없어서 못했던 것들을 하나씩 해보고 있어요. 요즘은 등산 동호회 활동이 제일 재밌네요.",
    author: "인생2막",
    author_membership: "regular",
    date: "2026.03.16",
    views: 278,
    likes: 54,
    comments: 28,
  },
  {
    id: 6,
    category: "friends",
    categoryName: "친구&모임",
    title: "LA 지역 중년 모임 있으신가요?",
    content: "LA에 사는 50대 남성입니다. 같이 골프나 등산 다닐 친구를 찾고 있어요.",
    author: "LA친구",
    author_membership: "premium",
    date: "2026.03.16",
    views: 145,
    likes: 23,
    comments: 19,
  },
  {
    id: 7,
    category: "love",
    categoryName: "연애와 썸",
    title: "카톡 답장이 늦어지면 불안해요",
    content: "새로 만난 분이 있는데, 카톡 답장이 조금만 늦어도 불안해져요. 이 나이에 이런 감정을 느끼다니...",
    author: "설렘반불안반",
    author_membership: "basic",
    date: "2026.03.15",
    views: 367,
    likes: 78,
    comments: 45,
  },
  {
    id: 8,
    category: "daily",
    categoryName: "일상&취미",
    title: "오늘 첫 줌미팅 했어요!",
    content: "New Coupling 줌미팅 처음 참여했는데, 생각보다 재밌었어요. 다들 친절하시고 좋은 분들이 많더라구요.",
    author: "줌데뷔",
    author_membership: "regular",
    date: "2026.03.15",
    views: 198,
    likes: 41,
    comments: 15,
  },
];

export const CommunityPage = () => {
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category") || "all";
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);
  const [searchQuery, setSearchQuery] = useState("");
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dailyTopic, setDailyTopic] = useState(null);
  const [newMembers, setNewMembers] = useState([]);
  const [weeklyPopularPosts, setWeeklyPopularPosts] = useState([]);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [memberPopup, setMemberPopup] = useState(null);
  const [customAds, setCustomAds] = useState(null); // null = not loaded; [] = loaded empty -> use defaults; [..] = use these
  const [detailAd, setDetailAd] = useState(null);
  const [applyAd, setApplyAd] = useState(null);
  const [applyForm, setApplyForm] = useState({ name: "", phone: "", note: "" });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  // Update selected category when URL changes
  useEffect(() => {
    setSelectedCategory(categoryFromUrl);
  }, [categoryFromUrl]);

  useEffect(() => {
    restoreScrollPosition();
  }, []);

  // Fetch admin-managed sidebar ads (fallback to default hard-coded ads if none)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ads`);
        if (!res.ok) throw new Error("ads load failed");
        const data = await res.json();
        if (!cancelled) setCustomAds(data.ads || []);
      } catch (err) {
        if (!cancelled) setCustomAds([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return date.toLocaleDateString("ko-KR");
  };

  // Get initials from username
  const getInitials = (name) => {
    if (!name) return "?";
    return name.slice(0, 2);
  };

  // Fetch posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const categoryParam = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
        const response = await fetch(`${API_URL}/api/posts${categoryParam}`);
        
        if (response.ok) {
          const apiPosts = await response.json();
          setPosts(apiPosts);
        }
      } catch (error) {
        console.error("Failed to fetch posts:", error);
        setPosts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, [selectedCategory]);

  // Fetch daily topic and popular members
  useEffect(() => {
    const fetchExtras = async () => {
      try {
        const [topicRes, membersRes, popularPostsRes] = await Promise.all([
          fetch(`${API_URL}/api/community/daily-topic`),
          fetch(`${API_URL}/api/community/new-members`),
          fetch(`${API_URL}/api/community/weekly-popular-posts`),
        ]);
        if (topicRes.ok) setDailyTopic(await topicRes.json());
        if (membersRes.ok) {
          const data = await membersRes.json();
          setNewMembers(data.members || []);
        }
        if (popularPostsRes.ok) {
          const data = await popularPostsRes.json();
          setWeeklyPopularPosts(data.posts || []);
        }
      } catch (e) { console.error("Extras fetch error:", e); }
    };
    fetchExtras();
  }, []);

  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === "all" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get pinned notice posts (공지글 상단 고정)
  const apiNoticePosts = filteredPosts.filter(post => post.is_notice || post.is_event_notice);

  // Get regular posts (excluding notice posts)
  const regularPosts = filteredPosts.filter(post => !post.is_notice && !post.is_event_notice && !post.isNotice);

  const selectedCategoryInfo = categories.find(c => c.id === selectedCategory);

  const handleWritePost = () => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    const categoryParam = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
    navigate(`/community/write${categoryParam}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} defaultTab="register" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        {/* Page Title - Sticky */}
        <div className="sticky top-16 z-30 bg-[#FAFAF9] py-3 md:py-4 -mx-4 md:-mx-6 px-4 md:px-6">
          <div className="flex items-center gap-3">
            <h1 className="text-lg md:text-2xl font-bold text-stone-800 flex-shrink-0">중년 싱글을 위한 힐링 공간</h1>
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
              <input
                type="text"
                placeholder="검색어를 입력하세요"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white rounded-full border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 text-sm"
                data-testid="search-input"
              />
            </div>
            <Button 
              onClick={handleWritePost}
              className="btn-primary flex items-center gap-2 shadow-md text-sm flex-shrink-0" 
              data-testid="write-post-btn"
            >
              <PenSquare className="w-4 h-4" />
              <span className="hidden sm:inline">글쓰기</span>
            </Button>
          </div>
        </div>
        <p className="text-stone-500 text-sm leading-relaxed mb-6 hidden md:block" data-testid="community-description">
          중년들의 삶과 이야기가 따뜻하게 모이는 공간<br />
          <span className="text-stone-400">연애, 여행, 건강, 취미, 일상, 고민 — 우리의 결로 자연스럽게 나눠요 🌿</span>
        </p>

        {/* Mobile Category Scroll */}
        <div className="lg:hidden mb-4 -mx-6 px-6" data-testid="mobile-category-scroll">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {categories.map((category) => {
              const IconComponent = category.icon;
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    isSelected
                      ? "bg-burgundy-500 text-white shadow-sm"
                      : "bg-white text-stone-600 border border-stone-200"
                  }`}
                >
                  <IconComponent className={`w-3.5 h-3.5 ${isSelected ? "text-white" : "text-stone-400"}`} />
                  {category.title}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          {/* Left Sidebar - Categories (Desktop only) */}
          <aside className="w-64 flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 sticky top-24">
              <div className="px-2 mb-4">
                <h2 className="font-serif text-lg font-bold text-stone-900 leading-tight">이야기 카테고리</h2>
                <p className="text-[11px] text-stone-400 mt-0.5">우리 결에 맞는 주제를 골라보세요</p>
              </div>
              <nav className="space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto pr-2 scrollbar-thin">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  const isSelected = selectedCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                        isSelected 
                          ? `${category.bgColor} ${category.color} font-medium` 
                          : "text-stone-600 hover:bg-stone-50"
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      <IconComponent className={`w-5 h-5 ${isSelected ? category.color : "text-stone-400"}`} />
                      <span className="text-sm">{category.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content - Posts */}
          <main className="flex-1 min-w-0 lg:max-w-[65%]">

            {/* Category Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {selectedCategoryInfo && (
                  <>
                    <selectedCategoryInfo.icon className={`w-5 h-5 ${selectedCategoryInfo.color}`} />
                    <h2 className="font-bold text-stone-800">{selectedCategoryInfo.title}</h2>
                  </>
                )}
                <span className="text-stone-400 text-sm">({filteredPosts.length}개의 글)</span>
              </div>
              <select className="text-sm text-stone-600 bg-transparent border-none focus:outline-none cursor-pointer">
                <option>최신순</option>
                <option>인기순</option>
                <option>댓글순</option>
              </select>
            </div>

            {/* Quick Post Composer (전체글일 때만 표시) */}
            {selectedCategory === "all" && (
              <QuickPostComposer
                user={user}
                token={token}
                isAuthenticated={isAuthenticated}
                setShowAuth={setShowAuth}
                navigate={navigate}
                onPostCreated={(newPost) => setPosts((prev) => [newPost, ...prev])}
              />
            )}

            {/* Posts List */}
            <div className="space-y-2">
              {regularPosts.map((post) => (
                <article
                  key={post.id}
                  onClick={() => { saveScrollPosition(); navigate(`/community/post/${post.id}`); }}
                  className="bg-white rounded-lg shadow-sm border border-stone-100 px-3 py-1.5 hover:shadow-md transition-shadow cursor-pointer"
                  data-testid={`post-${post.id}`}
                >
                  <div className="flex gap-2.5">
                    {/* Profile Avatar */}
                    <AuthorProfilePopover authorId={post.author_id} authorName={post.author} authorMembership={post.author_membership}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-burgundy-300 transition-all ${
                        post.author_membership === 'vip' ? 'bg-purple-400' :
                        post.author_membership === 'premium' ? 'bg-amber-400' :
                        post.author_membership === 'regular' ? 'bg-emerald-400' : 'bg-slate-400'
                      }`}>
                        <span className="text-white font-bold text-xs">
                          {getInitials(post.author)}
                        </span>
                      </div>
                    </AuthorProfilePopover>
                    
                    <div className="flex-1 min-w-0">
                      {/* Author & Time + Action Icons */}
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                          <span className="text-stone-500 text-xs truncate">@{post.author}</span>
                          {post.author_membership && (
                            <MembershipBadge membership={post.author_membership} size="sm" />
                          )}
                          <DocumentVerifiedBadge verified={post.author_doc_verified} size="sm" />
                          <span className="text-stone-400 text-xs hidden sm:inline">·</span>
                          <span className="text-stone-400 text-xs hidden sm:inline">
                            {getRelativeTime(post.created_at || post.date)}
                          </span>
                          {/* Category Badge */}
                          <span className={`hidden sm:inline-block text-xs font-medium px-1.5 py-0.5 rounded ml-1 ${
                            categories.find(c => c.id === post.category)?.bgColor
                          } ${categories.find(c => c.id === post.category)?.color}`}>
                            {post.categoryName}
                          </span>
                        </div>
                        {/* Action Icons - Top Right */}
                        <div className="flex items-center gap-2 sm:gap-3 text-stone-400 flex-shrink-0">
                          <span className="flex items-center gap-1 text-xs">
                            <MessageSquare className="w-3 h-3" />
                            {post.comments}
                          </span>
                          <span className="flex items-center gap-1 text-xs">
                            <ThumbsUp className="w-3 h-3" />
                            {post.likes}
                          </span>
                          <span className="flex items-center gap-1 text-xs">
                            <Eye className="w-3 h-3" />
                            {post.views}
                          </span>
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h3 className="font-semibold text-sm text-stone-800 mb-0.5 hover:text-burgundy-500 transition-colors truncate">
                        {post.title}
                      </h3>
                      
                      {/* Content Preview */}
                      <p className="text-stone-500 text-xs line-clamp-1">
                        {post.content}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center gap-2 mt-8">
              <button className="w-8 h-8 rounded-lg bg-burgundy-500 text-white text-sm font-medium">1</button>
              <button className="w-8 h-8 rounded-lg bg-white border border-stone-200 text-stone-600 text-sm hover:bg-stone-50">2</button>
              <button className="w-8 h-8 rounded-lg bg-white border border-stone-200 text-stone-600 text-sm hover:bg-stone-50">3</button>
              <span className="text-stone-400">...</span>
              <button className="w-8 h-8 rounded-lg bg-white border border-stone-200 text-stone-600 text-sm hover:bg-stone-50">10</button>
            </div>
          </main>

          {/* Right Sidebar - Ad Space */}
          <aside className="w-64 flex-shrink-0 hidden lg:block">
            <div className="space-y-4">
              {/* 오늘의 신규멤버 */}
              <style>{`
                @keyframes fadeSlideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
              `}</style>
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 relative" data-testid="new-members-widget">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-bold text-stone-800">오늘의 신규멤버</h3>
                    {newMembers.length > 0 && (
                      <span className="animate-pulse inline-block w-2 h-2 bg-emerald-400 rounded-full" />
                    )}
                  </div>
                  {newMembers.length > 3 && (
                    <button onClick={() => setShowAllMembers(!showAllMembers)} className="text-[10px] text-burgundy-500 hover:underline" data-testid="show-all-members-btn">
                      {showAllMembers ? "접기" : `전체 ${newMembers.length}명`}
                    </button>
                  )}
                </div>
                {newMembers.length > 0 ? (
                  <div className="space-y-1">
                    {(showAllMembers ? newMembers : newMembers.slice(0, 3)).map((member, idx) => (
                      <div
                        key={member.user_id || idx}
                        className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-stone-50 cursor-pointer transition-colors group"
                        style={{ animation: `fadeSlideIn 0.4s ease ${idx * 0.1}s both` }}
                        onClick={() => setMemberPopup(memberPopup?.user_id === member.user_id ? null : member)}
                        data-testid={`new-member-${idx}`}
                      >
                        <span className="text-[10px] font-bold text-stone-400 w-4 text-center">{idx + 1}</span>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                          member.gender === "female" ? "bg-rose-100 text-rose-500" : "bg-blue-100 text-blue-500"
                        }`}>
                          {member.gender === "female" ? "F" : "M"}
                        </span>
                        <span className="text-xs font-medium text-stone-700 truncate flex-1 group-hover:text-burgundy-500 transition-colors">{member.name}</span>
                        <span className="text-[9px] text-emerald-500 font-bold animate-pulse">NEW</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-stone-400 text-center py-3">아직 데이터가 없습니다</p>
                )}
                {/* 멤버 프로필 팝업 */}
                {memberPopup && (
                  <div className="mt-3 p-3 bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-200 rounded-xl" data-testid="member-popup" style={{ animation: 'fadeSlideIn 0.3s ease' }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          memberPopup.gender === "female" ? "bg-rose-100 text-rose-500" : "bg-blue-100 text-blue-500"
                        }`}>
                          {memberPopup.gender === "female" ? "F" : "M"}
                        </span>
                        <div>
                          <p className="text-sm font-bold text-stone-800">{memberPopup.name}</p>
                          <p className="text-[10px] text-stone-400">{memberPopup.membership === "vip" ? "VIP" : memberPopup.membership === "premium" ? "프리미엄" : (memberPopup.membership === "regular" || memberPopup.membership === "standard") ? "정회원" : "일반회원"}</p>
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setMemberPopup(null); }} className="text-stone-400 hover:text-stone-600">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2 text-stone-600">
                        <MapPin className="w-3 h-3 text-stone-400" />
                        <span>{memberPopup.location || "미입력"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-stone-600">
                        <Calendar className="w-3 h-3 text-stone-400" />
                        <span>{memberPopup.birth_year ? `${memberPopup.birth_year}년생` : "미입력"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-stone-600">
                        <Users className="w-3 h-3 text-stone-400" />
                        <span>{memberPopup.gender === "female" ? "여성" : "남성"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 이번주 인기글 */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4" data-testid="weekly-popular-posts-widget">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <h3 className="text-sm font-bold text-stone-800">이번주 인기글</h3>
                    {weeklyPopularPosts.length > 0 && (
                      <span className="animate-pulse inline-block w-2 h-2 bg-orange-400 rounded-full" />
                    )}
                  </div>
                  {weeklyPopularPosts.length > 3 && (
                    <button onClick={() => setShowAllPosts(!showAllPosts)} className="text-[10px] text-burgundy-500 hover:underline" data-testid="show-all-posts-btn">
                      {showAllPosts ? "접기" : `전체 ${weeklyPopularPosts.length}개`}
                    </button>
                  )}
                </div>
                {weeklyPopularPosts.length > 0 ? (
                  <div className="space-y-1">
                    {(showAllPosts ? weeklyPopularPosts : weeklyPopularPosts.slice(0, 3)).map((post, idx) => (
                      <div
                        key={post.id || idx}
                        className="flex items-start gap-2 py-1.5 px-2 rounded-lg hover:bg-stone-50 cursor-pointer transition-colors group"
                        style={{ animation: `fadeSlideIn 0.4s ease ${idx * 0.1}s both` }}
                        onClick={() => navigate(`/community/post/${post.id}`)}
                        data-testid={`popular-post-${idx}`}
                      >
                        <span className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                          idx === 0 ? "bg-orange-100 text-orange-600" :
                          idx === 1 ? "bg-amber-100 text-amber-600" :
                          idx === 2 ? "bg-yellow-100 text-yellow-600" : "bg-stone-100 text-stone-400"
                        }`}>
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-stone-700 truncate group-hover:text-burgundy-500 transition-colors leading-snug">{post.title}</p>
                          <p className="text-[10px] text-stone-400 mt-0.5">
                            {post.author} · 조회 {post.views || 0} · 댓글 {post.comment_count || 0}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-stone-400 text-center py-3">아직 데이터가 없습니다</p>
                )}
              </div>

              {/* 오늘의 토론 배너 */}
              {dailyTopic && (
                <div 
                  className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => { setSelectedCategory("daily_topic"); navigate("/community/write?category=daily_topic&prefill=" + encodeURIComponent(`[오늘의 토론] ${dailyTopic.topic}\n\n나의 생각:`)); }}
                  data-testid="daily-topic-banner"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <h3 className="text-sm font-bold text-orange-600">오늘의 토론</h3>
                      {dailyTopic.comment_count > 0 && (
                        <span className="animate-pulse inline-block w-2 h-2 bg-orange-400 rounded-full" />
                      )}
                    </div>
                    {dailyTopic.comment_count > 0 && (
                      <span className="text-[10px] text-stone-400">{dailyTopic.comment_count}개의 의견</span>
                    )}
                  </div>
                  <p className="text-xs text-stone-700 line-clamp-2 leading-relaxed mb-2">{dailyTopic.topic}</p>
                  <span className="inline-flex items-center text-[11px] font-semibold text-orange-600 hover:text-orange-700 transition-colors">참여하기 &rarr;</span>
                </div>
              )}

              {/* 자기소개 릴레이 배너 */}
              <div 
                className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 cursor-pointer hover:shadow-md transition-all"
                onClick={() => { setSelectedCategory("intro"); navigate("/community/write?category=intro"); }}
                data-testid="intro-relay-banner"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Hand className="w-4 h-4 text-cyan-500" />
                  <h3 className="text-sm font-bold text-cyan-600">30초 자기소개 릴레이</h3>
                </div>
                <p className="text-xs text-stone-700 leading-relaxed mb-2">나를 소개하고, 인연을 찾아보세요!</p>
                <span className="inline-flex items-center text-[11px] font-semibold text-cyan-600 hover:text-cyan-700 transition-colors">소개하기 &rarr;</span>
              </div>

              {/* 글로 만나는 나의 인연 찾기 */}
              <MyPostsCard isAuthenticated={isAuthenticated} setShowAuth={setShowAuth} />

              {/* 연애 성향 테스트 CTA */}
              <div 
                className="bg-gradient-to-br from-stone-400 to-stone-500 rounded-2xl p-5 cursor-pointer hover:from-stone-300 hover:to-stone-400 transition-all overflow-hidden relative"
                onClick={() => navigate("/love-test")}
                data-testid="love-test-sidebar-cta"
              >
                <div className="absolute top-3 right-3 w-20 h-20 bg-white/10 rounded-full pointer-events-none" />
                <Heart className="w-8 h-8 text-rose-300 mb-3" />
                <p className="text-white font-bold text-sm mb-1">나의 연애 유형은?</p>
                <p className="text-stone-50/90 text-xs mb-3 leading-relaxed">3분이면 끝!<br />연애 성향 테스트</p>
                <span className="inline-block bg-rose-500/30 text-rose-50 text-xs font-medium px-3 py-1 rounded-full">테스트 시작 &rarr;</span>
              </div>

              {/* 비공개 상담소 카드 */}
              <div 
                className="bg-gradient-to-br from-stone-400 to-stone-500 rounded-2xl p-5 cursor-pointer hover:from-stone-300 hover:to-stone-400 transition-all overflow-hidden relative"
                onClick={() => navigate("/private-counseling")}
                data-testid="private-counseling-sidebar-card"
              >
                <div className="absolute top-3 right-3 w-20 h-20 bg-white/10 rounded-full" />
                <ShieldCheck className="w-8 h-8 text-emerald-300 mb-3" />
                <p className="text-white font-bold text-sm mb-1">비공개 상담소</p>
                <p className="text-stone-50/90 text-xs mb-3 leading-relaxed">익명으로 고민을 올리면<br />전문 코치가 답변해 드려요</p>
                <span className="inline-block bg-emerald-500/30 text-emerald-50 text-xs font-medium px-3 py-1 rounded-full">상담하기 &rarr;</span>
              </div>

              {/* === 사이드바 광고: 관리자 페이지에서 등록·관리. 미등록 시 기본 광고 표시 === */}
              {(() => {
                const resolveAdImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_URL}${u}`);
                const defaultAds = [
                  { id: "d1", title: "제주 럭셔리 커플 여행", description: "특별한 만남, 특별한 여행으로",
                    image_url: "https://images.unsplash.com/photo-1661175886174-b23038ae89c9?w=400&h=250&fit=crop",
                    cta_label: "자세히 보기", cta_style: "light", link_url: "" },
                  { id: "d2", title: "AI 맞춤 매칭 서비스", description: "당신에게 딱 맞는 인연을 찾아드립니다",
                    image_url: "https://images.unsplash.com/photo-1546560534-a9644bda527a?w=400&h=250&fit=crop",
                    cta_label: "무료 체험", cta_style: "dark", link_url: "" },
                  { id: "d3", title: "경복궁 한복 데이트", description: "전통과 로맨스가 만나는 곳",
                    image_url: "https://images.unsplash.com/photo-1711017791515-871d74b83286?w=400&h=250&fit=crop",
                    cta_label: "예약하기", cta_style: "light", link_url: "" },
                  { id: "d4", title: "소셜 와인 클래스", description: "와인과 함께하는 소셜 모임",
                    image_url: "https://images.unsplash.com/photo-1615797107871-799f02b95d07?w=400&h=250&fit=crop",
                    cta_label: "신청하기", cta_style: "light", link_url: "" },
                  { id: "d5", title: "부산 해운대 오션뷰", description: "바다가 보이는 프리미엄 숙소",
                    image_url: "https://images.unsplash.com/photo-1724618194655-c3c12254d61c?w=400&h=250&fit=crop",
                    cta_label: "확인하기", cta_style: "light", link_url: "" },
                  { id: "d6", title: "1:1 프로필 코칭", description: "전문가가 알려주는 매력적인 프로필",
                    image_url: "https://images.unsplash.com/photo-1669446618431-23aed5898194?w=400&h=250&fit=crop",
                    cta_label: "상담 신청", cta_style: "dark", link_url: "" },
                  { id: "d7", title: "사찰 힐링 템플스테이", description: "마음의 여유를 찾는 특별한 시간",
                    image_url: "https://images.unsplash.com/photo-1749733600983-93d2739513d0?w=400&h=250&fit=crop",
                    cta_label: "알아보기", cta_style: "light", link_url: "" },
                  { id: "d8", title: "커플 스냅 포토", description: "소중한 순간을 아름답게 기록",
                    image_url: "https://images.unsplash.com/photo-1767600468766-cf1291fe5439?w=400&h=250&fit=crop",
                    cta_label: "촬영 예약", cta_style: "light", link_url: "" },
                ];
                const list = (customAds && customAds.length > 0) ? customAds : (customAds === null ? [] : defaultAds);
                return list.map((ad) => {
                  const ctaClasses = ad.cta_style === "dark"
                    ? "text-white bg-stone-700"
                    : "text-stone-600 bg-stone-100";
                  const Card = (
                    <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-1 border-b border-amber-100">
                        <span className="text-[10px] text-amber-700 font-bold tracking-wider uppercase">Upcoming Event</span>
                      </div>
                      {ad.image_url ? (
                        <img
                          src={resolveAdImg(ad.image_url)}
                          alt={ad.title}
                          className="w-full h-36 object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        // 이미지 미설정 시: 깔끔한 그라데이션 카드 (음식 사진 자동 폴백 제거)
                        <div className="w-full h-36 bg-gradient-to-br from-stone-100 via-rose-50 to-amber-50 flex items-center justify-center">
                          <div className="text-center px-4">
                            <p className="text-xs font-bold tracking-[0.2em] text-burgundy-500/70 uppercase">New Coupling</p>
                            <p className="mt-1 text-[11px] text-stone-500">이벤트 이미지 준비 중</p>
                          </div>
                        </div>
                      )}
                      <div className="p-3">
                        <p className="text-sm font-bold text-stone-800">{ad.title}</p>
                        {ad.description ? (
                          <p className="text-xs text-stone-500 mt-1">{ad.description}</p>
                        ) : null}
                        <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${ctaClasses}`}>
                          {ad.cta_label || "자세히 보기"} &rarr;
                        </span>
                      </div>
                    </div>
                  );
                  return (
                    <button
                      key={ad.id}
                      type="button"
                      onClick={() => setDetailAd(ad)}
                      className="text-left w-full"
                      data-testid={`community-ad-${ad.id}`}
                    >
                      {Card}
                    </button>
                  );
                });
              })()}
            </div>
          </aside>
        </div>
      </div>

      {/* Ad Detail Modal */}
      {detailAd && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
          onClick={() => setDetailAd(null)}
          data-testid="ad-detail-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Image */}
            <div className="relative shrink-0">
              <img
                src={detailAd.image_url?.startsWith("http") ? detailAd.image_url : `${API_URL}${detailAd.image_url}`}
                alt={detailAd.title}
                className="w-full h-56 object-cover"
              />
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className="px-2.5 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-full text-[10px] font-bold tracking-wider uppercase shadow-md">
                  Upcoming Event
                </span>
                {detailAd.action_type === "event_apply" && (
                  <span className="px-2.5 py-1 bg-amber-500 text-white rounded-full text-[10px] font-bold flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    이벤트 신청
                  </span>
                )}
              </div>
              <button
                onClick={() => setDetailAd(null)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white shadow-lg"
                data-testid="ad-detail-close"
              >
                <X className="w-4 h-4 text-stone-700" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              <h2 className="text-xl font-bold text-stone-800 mb-1.5">{detailAd.title}</h2>
              {detailAd.description ? (
                <p className="text-sm text-stone-600 leading-relaxed">{detailAd.description}</p>
              ) : null}

              {detailAd.event_details ? (
                <div className="mt-5 p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl">
                  <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    행사 일정 및 안내
                  </p>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
                    {detailAd.event_details}
                  </p>
                </div>
              ) : null}

              {detailAd.link_url && detailAd.action_type !== "event_apply" ? (
                <div className="mt-5 p-3 bg-stone-50 border border-stone-200 rounded-xl flex items-center gap-2 text-xs text-stone-500">
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{detailAd.link_url}</span>
                </div>
              ) : null}
            </div>

            {/* Footer / CTA */}
            <div className="px-6 py-4 border-t border-stone-100 bg-stone-50 flex items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDetailAd(null)}
                className="flex-1"
                data-testid="ad-detail-cancel"
              >
                닫기
              </Button>
              {detailAd.action_type === "event_apply" ? (
                <Button
                  type="button"
                  onClick={() => {
                    const ad = detailAd;
                    setDetailAd(null);
                    setApplyAd(ad);
                    setApplyForm({
                      name: user?.username || "",
                      phone: user?.phone || "",
                      note: "",
                    });
                  }}
                  className="flex-1 btn-primary"
                  data-testid="ad-detail-apply"
                >
                  <Calendar className="w-4 h-4 mr-1.5" />
                  {detailAd.cta_label || "신청하기"}
                </Button>
              ) : detailAd.link_url ? (
                <Button
                  type="button"
                  onClick={() => {
                    const url = detailAd.link_url;
                    setDetailAd(null);
                    if (url.startsWith("/")) {
                      navigate(url);
                    } else {
                      window.open(url, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="flex-1 btn-primary"
                  data-testid="ad-detail-link"
                >
                  {detailAd.cta_label || "자세히 보기"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => setDetailAd(null)}
                  className="flex-1 btn-primary"
                  data-testid="ad-detail-ok"
                >
                  확인
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Event Apply Modal */}
      {applyAd && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setApplyAd(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            data-testid="ad-apply-modal"
          >
            <div className="relative">
              <img src={(applyAd.image_url?.startsWith("http") ? applyAd.image_url : `${API_URL}${applyAd.image_url}`)} alt={applyAd.title} className="w-full h-40 object-cover" />
              <button
                onClick={() => setApplyAd(null)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-white"
              >
                <X className="w-4 h-4 text-stone-700" />
              </button>
            </div>
            <div className="px-6 pt-5 pb-2">
              <h2 className="text-lg font-bold text-stone-800">{applyAd.title}</h2>
              {applyAd.description ? (
                <p className="text-sm text-stone-500 mt-1">{applyAd.description}</p>
              ) : null}
              {applyAd.event_details ? (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800 mb-1">📅 행사 일정 및 안내</p>
                  <p className="text-xs text-amber-900 whitespace-pre-wrap leading-relaxed">{applyAd.event_details}</p>
                </div>
              ) : null}
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!applyForm.name.trim() || !applyForm.phone.trim()) {
                  toast.error("이름과 연락처는 필수입니다.");
                  return;
                }
                setApplySubmitting(true);
                try {
                  const headers = { "Content-Type": "application/json" };
                  if (token) headers["Authorization"] = `Bearer ${token}`;
                  const res = await fetch(`${API_URL}/api/ads/${applyAd.id}/apply`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify(applyForm),
                  });
                  if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.detail || "신청 실패");
                  }
                  toast.success("신청이 접수되었습니다! 빠른 시일 내에 연락드리겠습니다.");
                  setApplyAd(null);
                  setApplyForm({ name: "", phone: "", note: "" });
                } catch (err) {
                  toast.error(err.message || "신청 처리 중 오류가 발생했습니다.");
                } finally {
                  setApplySubmitting(false);
                }
              }}
              className="px-6 py-4 space-y-3"
            >
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1 block">이름 *</label>
                <input
                  type="text"
                  value={applyForm.name}
                  onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400 text-sm"
                  placeholder="홍길동"
                  data-testid="ad-apply-name"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1 block">연락처 *</label>
                <input
                  type="tel"
                  value={applyForm.phone}
                  onChange={(e) => setApplyForm({ ...applyForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400 text-sm"
                  placeholder="010-1234-5678"
                  data-testid="ad-apply-phone"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1 block">메모 (선택)</label>
                <textarea
                  value={applyForm.note}
                  onChange={(e) => setApplyForm({ ...applyForm, note: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400 text-sm resize-none"
                  placeholder="문의 사항이나 요청 사항을 적어주세요."
                  data-testid="ad-apply-note"
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setApplyAd(null)} className="flex-1" disabled={applySubmitting}>
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={applySubmitting}
                  className="flex-1 btn-primary"
                  data-testid="ad-apply-submit"
                >
                  {applySubmitting ? "전송 중..." : (applyAd.cta_label || "신청하기")}
                </Button>
              </div>
              <p className="text-[10px] text-stone-400 text-center pt-1">신청 정보는 운영자에게만 전달되며 외부에 공개되지 않습니다.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
